 
import React, { useCallback, useMemo } from 'react';
import { Field, FieldTypes } from '@common-fe/common-fe';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { FieldStateKey, NON_ASSIGNABLE_VALUE, PermissionKey,REQUIRED_TEXT } from '@/common/constants';
import { AccountToPushToField } from '@/components/elements/AccountToPushToField';
import { useHideFields } from '@/modules/employer/components/SetupEmployer/hooks';
import { useSetupEmployerMode } from '@/modules/employer/components/SetupEmployer/hooks/useSetupEmployerMode';
import { BankInfo } from '@/modules/employer/components/SetupEmployer/SetupEmployer.types';
import { BankMethod } from '@/modules/employer/hooks/useBankMethods.query';
import { EmployerDto } from '@/modules/employer/types';
import useGetPermissionToEditOverridableField from '@/modules/user/hooks/useGetPermissionToEditOverridableField';

import { AccountMethods,AccountUsageValues } from '../AccountUsage.types';

const placeholder = 'Select bank account';
const defaultGiveUpDays = 30;
const ACCOUNT_PLACEHOLDER = 'The account used to push funds for contribution will be provided once organization is activated.';
  
const usePayrollContributionFields = (
  banks: BankInfo[],
  methods: BankMethod[],
  formattedData: EmployerDto,
  currentValue?: AccountUsageValues,
  activateMode?: boolean,
  isLoading?: boolean,
  hsaClearingAccount?: string,
  routingNumber?: string,
) => {
  const externalPayrollContributionsAccountMethodPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.EXTERNAL_PAYROLL_CONTRIBUTIONS_ACCOUNT_METHOD,
    FieldStateKey.EXTERNAL_PAYROLL_CONTRIBUTIONS_ACCOUNT_METHOD,
  );
  const payrollContributionsHoldPeriodPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.PAYROLL_CONTRIBUTIONS_HOLD_PERIOD,
    FieldStateKey.PAYROLL_CONTRIBUTIONS_HOLD_PERIOD,
  );
  const payrollContributionsGiveUpPeriodPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.CONTRIBUTION_PUSH_GIVE_UP_DAYS,
    FieldStateKey.CONTRIBUTION_PUSH_GIVE_UP_DAYS,
  );
  const { viewMode } = useSetupEmployerMode();

  const methodsOptions = useMemo(() => {
    const options = methods.map((option) => ({
      label: option.description,
      value: option.name,
    }));

    return options;
  }, [methods]);

  const getDefaultValue = useCallback((value?: string) => {
    const bankId = banks.find((bank) => bank.id === value)?.id;

    if (bankId) return bankId;

    if (formattedData?.normalProperties?.externalAccountEnable
      || value === NON_ASSIGNABLE_VALUE) return NON_ASSIGNABLE_VALUE;
    return '';
  }, [banks, formattedData]);

  const externalPayrollContributions = useMemo(
    () => currentValue?.payrollContributionBank,
    [currentValue],
  );

  const options = banks.map((bank) => ({
    key: bank.id,
    value: bank.description,
  }));

  const fields = useMemo(() => {
    if (isLoading) return [];

    return [
      {
        name: 'payrollContributionMethod',
        type: FieldTypes.Radio,
        disabled: viewMode,
        parentValueFn: (val) => !!val,
        label: 'Method',
        validator: yup.string().test({
          test: (val) => !!(val || formattedData?.overridableProperties?.externalPayrollContributionsAccountMethod),
          message: REQUIRED_TEXT,
        }),
        options: methodsOptions,
        value: formattedData?.overridableProperties?.externalPayrollContributionsAccountMethod,
        showRequireIcon: true,
        ...externalPayrollContributionsAccountMethodPermProps,
      },
      ...(currentValue?.payrollContributionMethod === AccountMethods.PULL_FUNDS ? [{
        name: 'payrollContributionBank',
        type: FieldTypes.Dropdown,
        disabled: viewMode,
        showRequireIcon: true,
        validator: activateMode ? VALIDATORS.REQUIRED_STRING : VALIDATORS.STRING,
        areSubFieldsShouldHide: !externalPayrollContributions || externalPayrollContributions === NON_ASSIGNABLE_VALUE,
        placeholder,
        singleMode: true,
        label: 'Bank Account',
        options,
        defaultValue: getDefaultValue(
          formattedData?.normalProperties?.externalPayrollContributionsAccount,
        ),
        value: getDefaultValue(
          formattedData?.normalProperties?.externalPayrollContributionsAccount,
        ),
      },
      ] : [
        {
          name: 'accountToPushTo',
          type: FieldTypes.Node,
          label: 'Account to push to',
          value: (
            <AccountToPushToField
              accountNumber={hsaClearingAccount}
              routingNumber={routingNumber}
              testId="payroll-contribution"
              accountPlaceholder={ACCOUNT_PLACEHOLDER}
            />
          ),
          ...externalPayrollContributionsAccountMethodPermProps,
        },
      ]),
      ...(currentValue?.payrollContributionMethod === AccountMethods.PUSH_FUNDS ? [
        {
          name: 'contributionPushGiveUpDays',
          type: FieldTypes.Mask,
          disabled: viewMode,
          label: 'Give up period (days)',
          value: `${formattedData?.overridableProperties?.contributionPushGiveUpDays}` || defaultGiveUpDays,
          ...payrollContributionsGiveUpPeriodPermProps,
          hasHorizontalLine: true,
          mask: '99',
        }] : [
        {
          name: 'payrollContributionHoldPeriod',
          type: FieldTypes.Mask,
          disabled: viewMode,
          label: 'Hold period',
          value: `${formattedData?.overridableProperties?.payrollContributionsHoldPeriod}` || defaultGiveUpDays,
          ...payrollContributionsHoldPeriodPermProps,
          hasHorizontalLine: true,
          mask: '99',
        }]),
    ] as Field[];
  }, [
    formattedData,
    isLoading,
    // methods,
    viewMode,
    externalPayrollContributionsAccountMethodPermProps,
    payrollContributionsHoldPeriodPermProps,
    payrollContributionsGiveUpPeriodPermProps,
    currentValue,
    hsaClearingAccount,
    methodsOptions,
    options,
    getDefaultValue,
    activateMode,
    externalPayrollContributions,
    routingNumber,
  ]);


  const formattedFields = useHideFields(fields);
  return formattedFields;
};

export default usePayrollContributionFields;
